import { scopedChannelApi } from '@/session/scopedClients';
import type { RequestScope } from '@/session/scopedTransport';
import { buildAsturHeaders } from '@/endpoints/channel/asturHeaders';
import type { CommonalityEnabled, CommonalityItem, CommonalitySubscriptionPostBody, CommonalitySubscriptionPostResponse } from '@/types/commonality';
import type { ChannelApiResponse } from '@/types/endpoints';
import { utils } from '@/utils';
import type { WriteRequestOptions } from '../../requestOptions';

function normalizeNumber(v: unknown): number | null {
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string' && v.trim() && Number.isFinite(Number(v))) return Number(v);
  return null;
}

export async function postSubscriptionCommonality(
  scope: RequestScope,
  args: { subscriptionId: number | string; body: CommonalitySubscriptionPostBody },
  options: WriteRequestOptions
): Promise<ChannelApiResponse<CommonalityItem>> {
  try {
    const res = await scopedChannelApi(scope).post<CommonalitySubscriptionPostResponse>(
      'atts/commonality/subscription',
      // The channel API expects the payload directly (not wrapped).
      // Legacy `utils.apiCallInstance.post(path, { data: body })` also sends `body`.
      args.body,
      {
        ...options,
        headers: buildAsturHeaders({ subscriptionId: args.subscriptionId }),
      }
    );

    const fromApi = res.data?.data?.commonality;
    if (!fromApi) {
      throw new Error('Invalid commonality save response');
    }

    const id = normalizeNumber(fromApi.id) ?? normalizeNumber(res.data?.data?.id) ?? 0;
    const subscription_id = normalizeNumber(fromApi.subscription_id);
    const global_set_id = normalizeNumber(fromApi.global_set_id);
    const grouping_id = normalizeNumber(fromApi.grouping_id);

    const enabledRaw = fromApi.enabled;
    const enabled: CommonalityEnabled = enabledRaw === true || enabledRaw === 1 ? 1 : 0;

    return {
      data: {
        id,
        created_at: fromApi.created_at ?? null,
        updated_at: fromApi.updated_at ?? null,
        commonality_key: fromApi.commonality_key,
        default_value_type: fromApi.default_value_type,
        default_value: fromApi.default_value,
        fallback_value_type: fromApi.fallback_value_type ?? null,
        fallback_value: fromApi.fallback_value ?? null,
        subscription_id,
        global_set_id,
        grouping_id,
        enabled,
        secondary_id: null,
        secondary_id_type: null,
      },
      status: res.status,
    };
  } catch (e) {
    return utils.error.handleAxiosError(e);
  }
}
